/**
 * Handles WebRTC signaling with one peer with which a valid connection has been established.
 * This is the base class for the PreviewMasterConnection and PreviewSlave connection classes.
 */
export class PreviewRtcPeerConnection {
    constructor(signalingChannel, connection, clientId, peerId) {
        this.signalingChannel = signalingChannel;
        this.connection = connection;
        this.clientId = clientId;
        this.peerId = peerId;
        this.isDisposed = false;
        this.canUseWebRTC = !!connection;
        if (!connection) {
            // Continue without WebRTC support, transferring previews over the SignalingChannel.
            return;
        }
        // Setup ice handling
        connection.onicecandidate = (event) => {
            let candidate = event.candidate;
            // The following check instead of a simple "if (candidate)" fixes error "Expected line: candidate:<candidate-str>".
            // https://bugzilla.mozilla.org/show_bug.cgi?id=1540614#c14
            if (candidate && candidate.candidate && candidate.candidate.length) {
                const msg = { type: 7 /* SignalingMessageType.RTCIceCandidate */, candidate: candidate, fromClientId: this.clientId, toClientId: peerId };
                this.signalingChannel.sendSignalingMessage(msg);
            }
        };
        // Handle data channel creation events, for data channels that we did not initiate ourselves.
        // Note that onDataChannelCreated will only be received after we (as client)
        // received and answered an offer from the initiator.
        connection.ondatachannel = (event) => {
            const channel = event.channel;
            this.handleDataChannelCreated(channel, false);
        };
    }
    /**
     * Creates and sends an offer to send data over the peer connection. Only call this function
     * after the peer also has a peer connection. The peer will receive this offer and
     * send an answer in the handleOffer() function.
     * Note that RTCPeerConnection won't start gathering candidates until setLocalDescription() is called.
     */
    sendOffer(options) {
        const peer = this.connection;
        if (!peer)
            return;
        // RTCPeerConnection’s createOffer method generates an SDP (Session Description Protocol ) object,
        // used for negotiating session capabilities between the peers.
        peer.createOffer(options)
            .then((sdp) => {
            // console.log(`Sending offer, setting local description:`, JSON.stringify(offer));
            const message = { type: 8 /* SignalingMessageType.RTCOffer */, offer: sdp, fromClientId: this.clientId, toClientId: this.peerId };
            peer.setLocalDescription(sdp).then(() => {
                this.signalingChannel.sendSignalingMessage(message);
            });
        })
            .catch((reason) => {
            console.error('Error when creating offer', reason);
        });
    }
    /**
     * Called when a the initiator sends us an offer.
    */
    handleOfferMessage(offer) {
        if (!this.canUseWebRTC)
            return;
        const peer = this.connection;
        console.log(`Received offer from peer ${this.peerId}, setting remote description and sending answer.`);
        const sdp = new RTCSessionDescription(offer.offer);
        peer
            .setRemoteDescription(sdp) // parsing the sessionDescription (SDP) from a remote.
            .then(() => {
            // Only create an answer when we are sure that the remote desciption was set
            // (Firefox will throw a "DOMException [InvalidStateError: "setRemoteDescription not called")" otherwise.
            // console.log(`Remote description was set. Sending answer to ${this.peerId}....`);
            peer.createAnswer()
                .then((answer) => {
                // console.log('Creating answer to offer, setting local description:', JSON.stringify(answer));
                const message = { type: 9 /* SignalingMessageType.RTCAnswer */, answer: answer, fromClientId: this.clientId, toClientId: offer.fromClientId };
                peer.setLocalDescription(answer)
                    .then(() => {
                    // console.log(`Sending answer to ${this.peerId}....`);
                    this.signalingChannel.sendSignalingMessage(message);
                })
                    .catch(e => {
                    console.error('Error setting local description:', e);
                });
            })
                .catch(e => {
                console.error('Error creating answer:', e);
            });
        });
    }
    handleAnswerMessage(answer) {
        if (!this.canUseWebRTC)
            return;
        console.log(`Received answer from peer ${this.peerId}. Setting remote description.`);
        const sdp = new RTCSessionDescription(answer.answer);
        // console.log('Handling answer to offer: setting remote description:', JSON.stringify(sdp.toJSON()));
        this.connection
            .setRemoteDescription(sdp)
            // .then(() => {
            //     console.log(`Done setting remote description of peer ${this.peerId}.`);
            // })
            .catch((e) => {
            console.error(`Failed setting remote description of peer ${this.peerId}: ${e}`);
        });
    }
    handleIceCandidateMessage(message) {
        if (!this.canUseWebRTC)
            return;
        this.connection
            .addIceCandidate(new RTCIceCandidate(message.candidate))
            .catch((e) => {
            throw e;
        });
    }
    // #region data channels
    handleDataChannelOpen(dataChannel) { }
    handleDataChannelClose(dataChannel) { }
    handlePeerMessage(message) { }
    handleDataChannelCreated(dataChannel, isInitiator) {
        console.log(`Signaling: data channel '${dataChannel.label}' was created with peer ${this.peerId}. Binary type: ${dataChannel.binaryType}. Initiator: ${isInitiator}.`);
        let pollCount = 0;
        const interval = setInterval(() => {
            pollCount++;
            const state = dataChannel.readyState;
            if (state === 'connecting') {
                if (pollCount === 5) {
                    console.log(`Stopped polling '${dataChannel.label}' after ${pollCount} attempts. The channel is not connected.`);
                    clearInterval(interval);
                }
                else
                    console.log(`Data channel '${dataChannel.label}' is still connecting...`);
            }
            else
                clearInterval(interval);
        }, 1000);
        dataChannel.onerror = (error) => {
            if (!this.isDisposed) // avoid "this channel is closed" errors after disposal
                console.error(`Error in data channel '${dataChannel.label}' with peer ${this.peerId}.`, error);
        };
        dataChannel.onopen = (ev) => {
            this.handleDataChannelOpen(dataChannel);
        };
        dataChannel.onclose = (event) => {
            // console.log(`Signaling: data data channel '${dataChannel.label}' with peer ${this.peerId} was closed.`);
            this.handleDataChannelClose(dataChannel);
        };
    }
    /**
     * Initiates a data channel with a peer.
     * @param otherClientId The client id of the peer.
     * @param label The name of the data channel.
     */
    createDataChannel(label, binaryType) {
        if (!this.canUseWebRTC)
            return;
        const peer = this.connection;
        const dataChannelInit = {};
        // Note that we only get onopen() events after sending an offer and receiving an answer
        const dataChannel = peer.createDataChannel(label, dataChannelInit);
        if (binaryType) {
            dataChannel.binaryType = binaryType;
        }
        this.handleDataChannelCreated(dataChannel, true);
    }
    // #endregion data channels
    dispose() {
        this.isDisposed = true;
    }
}
//# sourceMappingURL=preview-rtc-peer-connection.js.map